import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../api/GlobalContext';
import {
  NOTE_TYPES,
  PROFESSION_OPTIONS,
} from '../../constants/EmrNoteTypeMapping';
import { CPT_CODES } from '../../constants/CptCodes';

const VISIT_OPTIONS = NOTE_TYPES.map((n) => n.name);
const GREEN = '#1D9E75';
const GREEN_BG = '#f2faf7';

const PAYER_PALETTES = [
  { bg: '#e8f5ee', color: '#0F6E56' },
  { bg: '#e8f0fe', color: '#185FA5' },
  { bg: '#fef3e2', color: '#854F0B' },
  { bg: '#f3e8fd', color: '#6D28D9' },
  { bg: '#fde8f0', color: '#BE185D' },
  { bg: '#e0f2fe', color: '#0369A1' },
];

// Normalize legacy { rule, cond } format to new { rules } format.
// Every configured code is inherently "always bill" — it is always graded
// against the visit recording + note — so always_bill is forced true and the
// per-code "rules" are kept as optional conditions the grader also checks.
function normCode(c) {
  if (typeof c === 'string')
    return { code: c, desc: '', always_bill: true, rules: [] };
  let rules = [];
  if (Array.isArray(c.rules)) {
    rules = c.rules;
  } else if (c.cond) {
    rules = [c.cond];
  }
  return { code: c.code || '', desc: c.desc || '', always_bill: true, rules };
}

function normPayer(payer) {
  const profs = (payer.professions || []).map((p) => ({
    ...p,
    visits: (p.visits || []).map((v) => ({
      ...v,
      codes: (v.codes || []).map(normCode),
    })),
  }));
  return {
    ...payer,
    professions: profs,
    clinic_notes: payer.clinic_notes || '',
  };
}

function totalCodes(payer) {
  return (payer.professions || []).reduce(
    (a, p) =>
      a + (p.visits || []).reduce((b, v) => b + (v.codes || []).length, 0),
    0,
  );
}

function totalRules(payer) {
  return (payer.professions || []).reduce(
    (a, p) =>
      a +
      (p.visits || []).reduce(
        (b, v) =>
          b + (v.codes || []).reduce((s, c) => s + (c.rules || []).length, 0),
        0,
      ),
    0,
  );
}

function isMedicarePayer(payer) {
  return (
    payer?.payer_key === 'mc' ||
    payer?.payer_name?.toLowerCase() === 'medicare'
  );
}

export default function BillingRules({ apiClient: apiClientProp }) {
  const ctx = useContext(GlobalContext);
  const apiClient = apiClientProp || ctx?.apiClient;

  const [payers, setPayers] = useState([]);
  const [activePayerKey, setActivePayerKey] = useState(null);
  const [activeProfIdx, setActiveProfIdx] = useState(0);
  const [openVisits, setOpenVisits] = useState({});
  const [saveState, setSaveState] = useState('idle');
  const [addingPayer, setAddingPayer] = useState(false);
  const [newPayerName, setNewPayerName] = useState('');
  const [newPayerPlanType, setNewPayerPlanType] = useState('');
  const [editingPayerKey, setEditingPayerKey] = useState(null);
  const [editPayerName, setEditPayerName] = useState('');
  const [editPayerPlanType, setEditPayerPlanType] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteVisit, setConfirmDeleteVisit] = useState(null); // { vi, name }
  const [confirmDeleteProf, setConfirmDeleteProf] = useState(null); // { idx, prof }
  // This user's EMR Note Settings (note types from their EMR templates)
  const [emrNoteTypes, setEmrNoteTypes] = useState([]);
  const [emrNoteTypesLoaded, setEmrNoteTypesLoaded] = useState(false);
  const [addingCustomVisit, setAddingCustomVisit] = useState(false);
  const [customVisitText, setCustomVisitText] = useState('');
  // CPT modal
  const [showCodeModal, setShowCodeModal] = useState(null); // { vi }
  const [modalSearch, setModalSearch] = useState('');
  const [modalCustomDesc, setModalCustomDesc] = useState('');
  // Per-code edit state (editing the code/desc of an already-added code)
  const [editingCode, setEditingCode] = useState(null); // { vi, ci }
  const [editCodeNum, setEditCodeNum] = useState('');
  const [editCodeDesc, setEditCodeDesc] = useState('');
  // Per-code rule state
  const [editingRule, setEditingRule] = useState(null); // { vi, ci, ri }
  const [editRuleText, setEditRuleText] = useState('');
  const [deletingRule, setDeletingRule] = useState(null); // { vi, ci, ri }
  const [addingRule, setAddingRule] = useState(null); // { vi, ci }
  const [newRuleText, setNewRuleText] = useState('');
  // Drag-and-drop
  const dragRef = useRef(null); // { vi, ci }

  const initializedRef = useRef(false);
  const activePayerKeyRef = useRef(activePayerKey);
  useEffect(() => {
    activePayerKeyRef.current = activePayerKey;
  }, [activePayerKey]);

  useEffect(() => {
    if (!apiClient) return;
    apiClient
      .getBillingRules()
      .then((rsp) => {
        const loaded = (rsp?.payers || []).map(normPayer);
        setPayers(loaded);
        const def = loaded.find((p) => p.is_default) || loaded[0];
        if (def) setActivePayerKey(def.payer_key);
        initializedRef.current = true;
      })
      .catch(() => {
        initializedRef.current = true;
      });
  }, [apiClient]);

  // Pull this user's EMR Note Settings so the "+ Visit type" picker offers their
  // real EMR note types (configs with an EMR url, excluding the web app).
  useEffect(() => {
    if (!apiClient) return;
    if (!apiClient.getUserConfigs) {
      setEmrNoteTypesLoaded(true);
      return;
    }
    let cancelled = false;
    apiClient
      .getUserConfigs()
      .then((rsp) => {
        if (cancelled) return;
        const configs = Array.isArray(rsp?.configs) ? rsp.configs : [];
        const names = configs
          .filter(
            (c) => c?.emr_url && !c.emr_url.includes('app.comprehendpt.com'),
          )
          .map((c) => c?.name)
          .filter((n) => typeof n === 'string' && n.trim());
        const unique = [...new Set(names)];
        if (unique.length) setEmrNoteTypes(unique);
        setEmrNoteTypesLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setEmrNoteTypesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const saveTimerRef = useRef(null);
  const doSave = useCallback(
    (payersList, payerKey) => {
      const target = payersList.find((p) => p.payer_key === payerKey);
      if (!target || !apiClient) return;
      setSaveState('saving');
      apiClient
        .saveBillingRules(target)
        .then(() => {
          setSaveState('saved');
          setTimeout(() => setSaveState('idle'), 2000);
        })
        .catch(() => setSaveState('idle'));
    },
    [apiClient],
  );

  useEffect(() => {
    if (!initializedRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      doSave(payers, activePayerKeyRef.current);
    }, 1000);
    return () => clearTimeout(saveTimerRef.current);
  }, [payers, doSave]);

  const activePayer =
    payers.find((p) => p.payer_key === activePayerKey) || payers[0];
  const professions = activePayer?.professions || [];
  const activeProf = professions[activeProfIdx] || { prof: '', visits: [] };

  const mutatePayer = (updater) => {
    setPayers((prev) =>
      prev.map((p) => {
        if (p.payer_key !== activePayerKey) return p;
        const clone = JSON.parse(JSON.stringify(p));
        updater(clone);
        return clone;
      }),
    );
  };

  const addPayer = () => {
    const name = newPayerName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, '_');
    if (payers.find((p) => p.payer_key === key)) {
      toast.warning(`A payer named "${name}" already exists.`);
      return;
    }
    if (isMedicarePayer({ payer_key: key, payer_name: name })) {
      toast.warning('Medicare is a reserved payer and cannot be added.');
      return;
    }
    const newPayer = {
      payer_key: key,
      payer_name: name,
      plan_type: newPayerPlanType.trim(),
      is_default: false,
      professions: [],
      clinic_notes: '',
    };
    setPayers((prev) => [...prev, newPayer]);
    setActivePayerKey(key);
    setActiveProfIdx(0);
    setOpenVisits({});
    setAddingPayer(false);
    setNewPayerName('');
    setNewPayerPlanType('');
    apiClient.saveBillingRules(newPayer).catch(console.error);
  };

  const doDeletePayer = async () => {
    if (!confirmDelete) return;
    if (isMedicarePayer(confirmDelete)) {
      setConfirmDelete(null);
      return;
    }
    const key = confirmDelete.payer_key;
    const remaining = payers.filter((p) => p.payer_key !== key);
    setPayers(remaining);
    if (activePayerKey === key) {
      setActivePayerKey(remaining[0]?.payer_key || null);
      setActiveProfIdx(0);
    }
    setConfirmDelete(null);
    try {
      await apiClient.deletePayer(key);
    } catch (e) {
      console.error(e);
    }
  };

  const startEditPayer = (e, payer) => {
    e.stopPropagation();
    setEditingPayerKey(payer.payer_key);
    setEditPayerName(payer.payer_name);
    setEditPayerPlanType(payer.plan_type || '');
  };

  const saveEditPayer = (e, payer) => {
    e.stopPropagation();
    const name = isMedicarePayer(payer) ? payer.payer_name : editPayerName.trim();
    if (!name) return;
    setPayers((prev) =>
      prev.map((p) =>
        p.payer_key === payer.payer_key
          ? { ...p, payer_name: name, plan_type: editPayerPlanType.trim() }
          : p,
      ),
    );
    setEditingPayerKey(null);
  };

  const setClinicNotes = (val) => {
    mutatePayer((clone) => {
      clone.clinic_notes = val;
    });
  };

  const addProfession = (prof) => {
    if (!prof || professions.find((p) => p.prof === prof)) return;
    mutatePayer((clone) => clone.professions.push({ prof, visits: [] }));
    setActiveProfIdx(professions.length);
  };

  const removeProfession = (idx) => {
    mutatePayer((clone) => {
      clone.professions.splice(idx, 1);
    });
    // Keep the active profession pointing at the same one (or fall back to the
    // first / empty state when the active profession itself is removed).
    setActiveProfIdx((cur) => {
      if (idx < cur) return cur - 1;
      if (idx === cur) return 0;
      return cur;
    });
    setOpenVisits({});
    setConfirmDeleteProf(null);
  };

  const addVisitType = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    if ((activeProf.visits || []).find((v) => v.name === trimmed)) return;
    const vi = activeProf.visits.length;
    mutatePayer((clone) => {
      clone.professions[activeProfIdx].visits.push({ name: trimmed, codes: [] });
    });
    setOpenVisits((p) => ({ ...p, [`${activeProfIdx}-${vi}`]: true }));
  };

  const removeVisitType = (vi) => {
    mutatePayer((clone) => {
      clone.professions[activeProfIdx].visits.splice(vi, 1);
    });
    setOpenVisits((p) => {
      const next = {};
      // Reindex open-state keys for this profession after removal
      Object.keys(p).forEach((k) => {
        const [pi, idx] = k.split('-').map(Number);
        if (pi !== activeProfIdx) {
          next[k] = p[k];
        } else if (idx < vi) {
          next[k] = p[k];
        } else if (idx > vi) {
          next[`${pi}-${idx - 1}`] = p[k];
        }
      });
      return next;
    });
    setConfirmDeleteVisit(null);
  };

  const toggleVisit = (vi) => {
    const k = `${activeProfIdx}-${vi}`;
    setOpenVisits((p) => ({ ...p, [k]: !p[k] }));
  };

  const removeCode = (vi, ci) => {
    mutatePayer((clone) => {
      clone.professions[activeProfIdx].visits[vi].codes.splice(ci, 1);
    });
  };

  const addCodeFromModal = (vi, num, desc) => {
    mutatePayer((clone) => {
      const codes = clone.professions[activeProfIdx].visits[vi].codes;
      if (!codes.find((c) => c.code === num)) {
        codes.push({
          code: num,
          desc: desc || num,
          always_bill: true,
          rules: [],
        });
      }
    });
  };

  const reorderCode = (vi, fromCi, toCi) => {
    if (fromCi === toCi) return;
    mutatePayer((clone) => {
      const codes = clone.professions[activeProfIdx].visits[vi].codes;
      const [moved] = codes.splice(fromCi, 1);
      codes.splice(toCi, 0, moved);
    });
  };

  const startEditCode = (vi, ci, code, desc) => {
    setEditingRule(null);
    setAddingRule(null);
    setDeletingRule(null);
    setEditingCode({ vi, ci });
    setEditCodeNum(code);
    setEditCodeDesc(desc);
  };

  const cancelEditCode = () => {
    setEditingCode(null);
    setEditCodeNum('');
    setEditCodeDesc('');
  };

  const saveEditCode = () => {
    if (!editingCode) return;
    const { vi, ci } = editingCode;
    const num = editCodeNum.trim().toUpperCase();
    if (!num) return;
    mutatePayer((clone) => {
      const codes = clone.professions[activeProfIdx].visits[vi].codes;
      codes[ci] = {
        ...codes[ci],
        code: num,
        desc: editCodeDesc.trim() || num,
      };
    });
    cancelEditCode();
  };

  const startAddRule = (vi, ci) => {
    setEditingRule(null);
    setDeletingRule(null);
    setAddingRule({ vi, ci });
    setNewRuleText('');
  };

  const confirmAddRule = () => {
    if (!addingRule) return;
    const { vi, ci } = addingRule;
    const text = newRuleText.trim();
    if (text) {
      mutatePayer((clone) => {
        clone.professions[activeProfIdx].visits[vi].codes[ci].rules.push(text);
      });
    }
    setAddingRule(null);
    setNewRuleText('');
  };

  const startEditRule = (vi, ci, ri, currentText) => {
    setDeletingRule(null);
    setAddingRule(null);
    setEditingRule({ vi, ci, ri });
    setEditRuleText(currentText);
  };

  const saveEditRule = () => {
    if (!editingRule) return;
    const { vi, ci, ri } = editingRule;
    const text = editRuleText.trim();
    if (text) {
      mutatePayer((clone) => {
        clone.professions[activeProfIdx].visits[vi].codes[ci].rules[ri] = text;
      });
    }
    setEditingRule(null);
  };

  const startDeleteRule = (vi, ci, ri) => {
    setEditingRule(null);
    setAddingRule(null);
    setDeletingRule({ vi, ci, ri });
  };

  const confirmDeleteRule = () => {
    if (!deletingRule) return;
    const { vi, ci, ri } = deletingRule;
    mutatePayer((clone) => {
      clone.professions[activeProfIdx].visits[vi].codes[ci].rules.splice(ri, 1);
    });
    setDeletingRule(null);
  };

  const availableProfs = PROFESSION_OPTIONS.filter(
    (p) => !professions.find((x) => x.prof === p),
  );

  // Prefer this user's EMR Note Settings; fall back to the standard list only
  // once the pull has resolved empty (never flashing the const while loading).
  const visitTypeSource = emrNoteTypes.length
    ? emrNoteTypes
    : emrNoteTypesLoaded
      ? VISIT_OPTIONS
      : [];
  const availableVisitOptions = visitTypeSource.filter(
    (v) => !(activeProf.visits || []).find((x) => x.name === v),
  );

  const modalExistingCodes = showCodeModal
    ? (activeProf.visits[showCodeModal.vi]?.codes || []).map((c) => c.code)
    : [];

  const modalFiltered = CPT_CODES.filter((c) => {
    if (!modalSearch.trim()) return true;
    const q = modalSearch.toLowerCase();
    return (
      c.code.includes(q) ||
      c.desc.toLowerCase().includes(q) ||
      c.cat.toLowerCase().includes(q)
    );
  });

  const isCustomEntry =
    modalSearch.trim() &&
    !CPT_CODES.find(
      (c) => c.code.toLowerCase() === modalSearch.trim().toLowerCase(),
    );

  const inp = {
    fontSize: 13,
    padding: '7px 10px',
    borderRadius: 6,
    border: '0.5px solid #e0e0da',
    background: '#fff',
    color: '#333',
    outline: 'none',
  };
  const btnPrimary = {
    padding: '7px 14px',
    borderRadius: 6,
    border: 'none',
    background: GREEN,
    color: '#fff',
    fontSize: 13,
    cursor: 'pointer',
  };
  const btnSecondary = {
    padding: '7px 10px',
    borderRadius: 6,
    border: '0.5px solid #e0e0da',
    background: '#fff',
    color: '#888',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        background: '#f5f5f3',
        borderRadius: 16,
        padding: 24,
      }}
    >
      {/* Delete payer modal */}
      {confirmDelete && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '28px 28px 22px',
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#111',
                marginBottom: 8,
              }}
            >
              Delete payer billing rules?
            </div>
            <div
              style={{
                fontSize: 13,
                color: '#555',
                lineHeight: 1.6,
                marginBottom: 22,
              }}
            >
              You are deleting all billing rules for{' '}
              <strong>{confirmDelete.payer_name}</strong>. This cannot be
              undone.
            </div>
            <div
              style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}
            >
              <button
                style={btnSecondary}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                style={{ ...btnPrimary, background: '#ef4444' }}
                onClick={doDeletePayer}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete visit type modal */}
      {confirmDeleteVisit && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setConfirmDeleteVisit(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '28px 28px 22px',
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#111',
                marginBottom: 8,
              }}
            >
              Delete note type?
            </div>
            <div
              style={{
                fontSize: 13,
                color: '#555',
                lineHeight: 1.6,
                marginBottom: 22,
              }}
            >
              This removes <strong>{confirmDeleteVisit.name}</strong> and all of
              its codes from {activePayer?.payer_name} · {activeProf.prof}. This
              cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={btnSecondary}
                onClick={() => setConfirmDeleteVisit(null)}
              >
                Cancel
              </button>
              <button
                style={{ ...btnPrimary, background: '#ef4444' }}
                onClick={() => removeVisitType(confirmDeleteVisit.vi)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete profession modal */}
      {confirmDeleteProf && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setConfirmDeleteProf(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '28px 28px 22px',
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#111',
                marginBottom: 8,
              }}
            >
              Delete profession?
            </div>
            <div
              style={{
                fontSize: 13,
                color: '#555',
                lineHeight: 1.6,
                marginBottom: 22,
              }}
            >
              This removes <strong>{confirmDeleteProf.prof}</strong> and all of
              its visit types and codes from {activePayer?.payer_name}. This
              cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={btnSecondary}
                onClick={() => setConfirmDeleteProf(null)}
              >
                Cancel
              </button>
              <button
                style={{ ...btnPrimary, background: '#ef4444' }}
                onClick={() => removeProfession(confirmDeleteProf.idx)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CPT code search modal */}
      {showCodeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            setShowCodeModal(null);
            setModalSearch('');
            setModalCustomDesc('');
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 480,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 15, fontWeight: 600 }}>Add CPT code</div>
            <div style={{ fontSize: 13, color: '#888' }}>
              Adding to {activeProf.visits[showCodeModal.vi]?.name}
            </div>
            <input
              style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
              placeholder="Search by code or description, or type a custom code..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              autoFocus
            />
            <div
              style={{
                overflowY: 'auto',
                border: '0.5px solid #e0e0da',
                borderRadius: 8,
                flex: 1,
                minHeight: 180,
                maxHeight: 340,
              }}
            >
              {isCustomEntry && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderBottom: '0.5px solid #f0f0ea',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: '#eeedfe',
                      border: '0.5px solid #c5c2f7',
                      color: '#3c3489',
                      minWidth: 54,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {modalSearch.trim()}
                  </span>
                  <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="Description (optional)"
                    value={modalCustomDesc}
                    onChange={(e) => setModalCustomDesc(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addCodeFromModal(
                          showCodeModal.vi,
                          modalSearch.trim().toUpperCase(),
                          modalCustomDesc.trim() ||
                            modalSearch.trim().toUpperCase(),
                        );
                        setShowCodeModal(null);
                        setModalSearch('');
                        setModalCustomDesc('');
                      }
                    }}
                  />
                  <span
                    onClick={() => {
                      addCodeFromModal(
                        showCodeModal.vi,
                        modalSearch.trim().toUpperCase(),
                        modalCustomDesc.trim() ||
                          modalSearch.trim().toUpperCase(),
                      );
                      setShowCodeModal(null);
                      setModalSearch('');
                      setModalCustomDesc('');
                    }}
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: GREEN,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    + Add
                  </span>
                </div>
              )}
              {modalFiltered.length === 0 && !isCustomEntry ? (
                <div
                  style={{
                    padding: 20,
                    textAlign: 'center',
                    fontSize: 13,
                    color: '#aaa',
                  }}
                >
                  No codes found
                </div>
              ) : (
                modalFiltered.map((fc) => {
                  const added = modalExistingCodes.includes(fc.code);
                  return (
                    <div
                      key={fc.code}
                      onClick={
                        added
                          ? undefined
                          : () =>
                              addCodeFromModal(
                                showCodeModal.vi,
                                fc.code,
                                fc.desc,
                              )
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        cursor: added ? 'default' : 'pointer',
                        borderBottom: '0.5px solid #f0f0ea',
                        opacity: added ? 0.4 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!added)
                          e.currentTarget.style.background = '#f5f9ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '';
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          padding: '3px 8px',
                          borderRadius: 6,
                          background: '#f5f5f3',
                          border: '0.5px solid #e0e0da',
                          color: '#555',
                          minWidth: 54,
                          textAlign: 'center',
                        }}
                      >
                        {fc.code}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#111' }}>
                          {fc.desc}
                        </div>
                        <div
                          style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}
                        >
                          {fc.cat}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: added ? '#ccc' : GREEN,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {added ? 'Added' : '+ Add'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                style={btnSecondary}
                onClick={() => {
                  setShowCodeModal(null);
                  setModalSearch('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#111',
              marginBottom: 3,
            }}
          >
            Coding Rules
          </div>
          <div style={{ fontSize: 13, color: '#999' }}>
            Manage coding rules per insurance payer
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {saveState === 'saving' && (
            <span style={{ fontSize: 12, color: '#aaa' }}>Saving…</span>
          )}
          {saveState === 'saved' && (
            <span style={{ fontSize: 12, color: GREEN }}>Saved</span>
          )}
          {addingPayer ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                style={{ ...inp, width: 150 }}
                placeholder="Payer name"
                value={newPayerName}
                onChange={(e) => setNewPayerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addPayer();
                  if (e.key === 'Escape') {
                    setAddingPayer(false);
                    setNewPayerName('');
                    setNewPayerPlanType('');
                  }
                }}
                autoFocus
              />
              <input
                style={{ ...inp, width: 130 }}
                placeholder="Plan type (e.g. PPO)"
                value={newPayerPlanType}
                onChange={(e) => setNewPayerPlanType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addPayer();
                }}
              />
              <button style={btnPrimary} onClick={addPayer}>
                Add
              </button>
              <button
                style={btnSecondary}
                onClick={() => {
                  setAddingPayer(false);
                  setNewPayerName('');
                  setNewPayerPlanType('');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              style={{ ...btnSecondary }}
              onClick={() => setAddingPayer(true)}
            >
              + Add payer
            </button>
          )}
        </div>
      </div>

      {/* Payer grid */}
      {payers.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 10,
            marginBottom: 20,
          }}
        >
          {payers.map((payer, idx) => {
            const pal = PAYER_PALETTES[idx % PAYER_PALETTES.length];
            const isActive = payer.payer_key === activePayerKey;
            const locked = isMedicarePayer(payer);
            const initials = payer.payer_name
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase();
            const cc = totalCodes(payer);
            const rc = totalRules(payer);
            const isEditing = editingPayerKey === payer.payer_key;
            return (
              <div
                key={payer.payer_key}
                onClick={() => {
                  if (!isEditing) {
                    setActivePayerKey(payer.payer_key);
                    setActiveProfIdx(0);
                    setOpenVisits({});
                  }
                }}
                style={{
                  background: '#fff',
                  border: isActive
                    ? `1.5px solid ${GREEN}`
                    : '0.5px solid #e8e8e3',
                  borderRadius: 12,
                  padding: 16,
                  cursor: isEditing ? 'default' : 'pointer',
                  transition: 'border-color 0.15s',
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 9,
                    background: pal.bg,
                    color: pal.color,
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  {initials}
                </div>
                {isEditing ? (
                  <>
                    <input
                      style={{
                        ...inp,
                        width: '100%',
                        boxSizing: 'border-box',
                        marginBottom: 6,
                        fontSize: 13,
                        fontWeight: 600,
                        ...(locked
                          ? {
                              background: '#f5f5f3',
                              color: '#999',
                              cursor: 'not-allowed',
                            }
                          : {}),
                      }}
                      value={editPayerName}
                      readOnly={locked}
                      title={
                        locked ? "Medicare's name can't be changed" : undefined
                      }
                      onChange={(e) => setEditPayerName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditPayer(e, payer);
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                          setEditingPayerKey(null);
                        }
                      }}
                      autoFocus={!locked}
                    />
                    <input
                      style={{
                        ...inp,
                        width: '100%',
                        boxSizing: 'border-box',
                        marginBottom: 14,
                        fontSize: 12,
                      }}
                      placeholder="Plan type"
                      value={editPayerPlanType}
                      onChange={(e) => setEditPayerPlanType(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditPayer(e, payer);
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                          setEditingPayerKey(null);
                        }
                      }}
                      autoFocus={locked}
                    />
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#111',
                        marginBottom: 2,
                      }}
                    >
                      {payer.payer_name}
                    </div>
                    <div
                      style={{ fontSize: 12, color: '#aaa', marginBottom: 14 }}
                    >
                      {payer.plan_type || payer.payer_key}
                    </div>
                  </>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '3px 8px',
                        borderRadius: 999,
                        fontWeight: 500,
                        background: pal.bg,
                        color: pal.color,
                        border: `0.5px solid ${pal.color}44`,
                      }}
                    >
                      {cc} codes
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '3px 8px',
                        borderRadius: 999,
                        fontWeight: 500,
                        background: '#f5f5f3',
                        color: '#888',
                        border: '0.5px solid #e0e0da',
                      }}
                    >
                      {rc} rules
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    {isEditing ? (
                      <>
                        <span
                          style={{
                            fontSize: 12,
                            color: GREEN,
                            cursor: 'pointer',
                            fontWeight: 500,
                          }}
                          onClick={(e) => saveEditPayer(e, payer)}
                        >
                          Save
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            color: '#aaa',
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPayerKey(null);
                          }}
                        >
                          Cancel
                        </span>
                      </>
                    ) : (
                      <span
                        style={{
                          fontSize: 12,
                          color: '#ccc',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => startEditPayer(e, payer)}
                      >
                        Edit
                      </span>
                    )}
                    {!locked && (
                      <span
                        style={{
                          color: '#ddd',
                          cursor: 'pointer',
                          fontSize: 18,
                          lineHeight: 1,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(payer);
                        }}
                        title="Delete payer"
                      >
                        ×
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {payers.length === 0 && !addingPayer && (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#ccc',
            fontSize: 13,
          }}
        >
          No payers configured yet.
          <br />
          Click &ldquo;+ Add payer&rdquo; to get started.
        </div>
      )}

      {/* Clinic notes (per payer) */}
      {activePayer && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#111',
              marginBottom: 2,
            }}
          >
            Clinic notes{' '}
            <span style={{ fontWeight: 400, color: '#aaa', fontSize: 13 }}>
              — {activePayer.payer_name}
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>
            Visible to therapists during coding — specific to this payer
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              border: '0.5px solid #e0e0da',
              overflow: 'hidden',
            }}
          >
            <textarea
              style={{
                width: '100%',
                border: 'none',
                fontSize: 13,
                color: '#111',
                fontFamily: 'inherit',
                resize: 'none',
                lineHeight: 1.6,
                padding: 14,
                minHeight: 72,
                background: 'transparent',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              placeholder="Add notes for therapists (e.g. billing guidance, unit expectations, payer-specific workflows)..."
              value={activePayer.clinic_notes || ''}
              onChange={(e) => setClinicNotes(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Two-pane content */}
      {activePayer && (
        <div
          style={{
            display: 'flex',
            background: '#fff',
            borderRadius: 12,
            border: '0.5px solid #e8e8e3',
            overflow: 'hidden',
            minHeight: 520,
          }}
        >
          {/* Sidebar */}
          <div
            style={{
              width: 200,
              flexShrink: 0,
              borderRight: '0.5px solid #eeeeea',
              padding: '16px 0',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: '#bbb',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '0 16px',
                marginBottom: 8,
              }}
            >
              Profession
            </div>
            {availableProfs.length > 0 && (
              <div style={{ padding: '0 12px', marginBottom: 10 }}>
                <select
                  style={{
                    width: '100%',
                    fontSize: 13,
                    padding: '7px 9px',
                    borderRadius: 8,
                    border: '0.5px solid #e0e0da',
                    background: '#fff',
                    color: '#555',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addProfession(e.target.value);
                  }}
                >
                  <option value="">+ Add profession</option>
                  {availableProfs.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {professions.length === 0 && (
              <div
                style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: '#bbb',
                  fontSize: 12,
                  lineHeight: 1.7,
                }}
              >
                Add a profession
                <br />
                to get started
              </div>
            )}
            {professions.map((p, i) => {
              const cnt = (p.visits || []).reduce(
                (a, v) => a + (v.codes || []).length,
                0,
              );
              const active = i === activeProfIdx;
              return (
                <div
                  key={i}
                  onClick={() => {
                    setActiveProfIdx(i);
                    setOpenVisits({});
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '9px 16px',
                    cursor: 'pointer',
                    borderLeft: `2px solid ${active ? GREEN : 'transparent'}`,
                    background: active ? GREEN_BG : 'transparent',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      color: active ? '#111' : '#888',
                      fontWeight: active ? 500 : 400,
                    }}
                  >
                    {p.prof}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: active ? '#d0f0e4' : '#f5f5f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: active ? '#0F6E56' : '#bbb',
                        flexShrink: 0,
                      }}
                    >
                      {cnt}
                    </span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteProf({ idx: i, prof: p.prof });
                      }}
                      title="Delete profession"
                      style={{
                        color: '#e0e0da',
                        cursor: 'pointer',
                        fontSize: 16,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = '#ef4444')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = '#e0e0da')
                      }
                    >
                      ×
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Main */}
          <div style={{ flex: 1, padding: 20, minWidth: 0 }}>
            {professions.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 300,
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    color: '#ccc',
                    fontSize: 13,
                    lineHeight: 1.8,
                  }}
                >
                  ← Add a profession first
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 15, fontWeight: 500, color: '#111' }}
                    >
                      {activeProf.prof}
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                      {activePayer.payer_name} ·{' '}
                      {(activeProf.visits || []).length} visit types
                    </div>
                  </div>
                  {addingCustomVisit ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        style={{ ...inp, width: 160, fontSize: 12 }}
                        placeholder="Custom note type"
                        value={customVisitText}
                        onChange={(e) => setCustomVisitText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            addVisitType(customVisitText);
                            setCustomVisitText('');
                            setAddingCustomVisit(false);
                          }
                          if (e.key === 'Escape') {
                            setCustomVisitText('');
                            setAddingCustomVisit(false);
                          }
                        }}
                        autoFocus
                      />
                      <button
                        style={{ ...btnPrimary, fontSize: 12, padding: '5px 10px' }}
                        onClick={() => {
                          addVisitType(customVisitText);
                          setCustomVisitText('');
                          setAddingCustomVisit(false);
                        }}
                      >
                        Add
                      </button>
                      <button
                        style={{ ...btnSecondary, fontSize: 12, padding: '5px 8px' }}
                        onClick={() => {
                          setCustomVisitText('');
                          setAddingCustomVisit(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <select
                      style={{
                        padding: '5px 8px',
                        borderRadius: 7,
                        border: '0.5px solid #d8d8d3',
                        background: '#fff',
                        color: '#888',
                        fontSize: 12,
                        cursor: 'pointer',
                        outline: 'none',
                        maxWidth: 160,
                      }}
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__custom__') {
                          setAddingCustomVisit(true);
                        } else if (val) {
                          addVisitType(val);
                        }
                      }}
                    >
                      <option value="">+ Visit type</option>
                      {!emrNoteTypesLoaded && !emrNoteTypes.length && (
                        <option value="" disabled>
                          Loading note types…
                        </option>
                      )}
                      {availableVisitOptions.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                      <option value="__custom__">+ Custom note type…</option>
                    </select>
                  )}
                </div>

                {(activeProf.visits || []).length === 0 ? (
                  <div
                    style={{
                      padding: '48px 20px',
                      textAlign: 'center',
                      color: '#ccc',
                      fontSize: 13,
                      lineHeight: 1.8,
                    }}
                  >
                    No visit types configured.
                    <br />
                    Use &ldquo;+ Visit type&rdquo; to get started.
                  </div>
                ) : (
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    {activeProf.visits.map((visit, vi) => {
                      const k = `${activeProfIdx}-${vi}`;
                      const isOpen = !!openVisits[k];
                      const preview =
                        (visit.codes || []).map((c) => c.code).join(', ') ||
                        'No codes';

                      return (
                        <div
                          key={vi}
                          style={{
                            border: '0.5px solid #e8e8e3',
                            borderRadius: 10,
                            overflow: 'hidden',
                            background: '#fff',
                          }}
                        >
                          {/* Visit header */}
                          <div
                            onClick={() => toggleVisit(vi)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '12px 14px',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = '#fafaf8')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = '')
                            }
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              style={{
                                transition: 'transform 0.18s',
                                transform: isOpen ? 'rotate(90deg)' : 'none',
                                flexShrink: 0,
                              }}
                            >
                              <path
                                d="M3 2l4 3-4 3"
                                stroke="#bbb"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                background: '#818cf8',
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: '#111',
                                flex: 1,
                              }}
                            >
                              {visit.name}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                color: '#ccc',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {preview}
                            </span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteVisit({ vi, name: visit.name });
                              }}
                              title="Delete note type"
                              style={{
                                color: '#e0e0da',
                                cursor: 'pointer',
                                fontSize: 18,
                                lineHeight: 1,
                                flexShrink: 0,
                                paddingLeft: 4,
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.color = '#ef4444')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.color = '#e0e0da')
                              }
                            >
                              ×
                            </span>
                          </div>

                          {isOpen && (
                            <div
                              style={{
                                borderTop: '0.5px solid #f0f0ea',
                                background: '#fafaf8',
                              }}
                            >
                              {/* Code blocks */}
                              {(visit.codes || []).map((c, ci) => {
                                const rules = c.rules || [];
                                const isMedicare = isMedicarePayer(activePayer);
                                const atRuleLimit =
                                  !isMedicare && rules.length >= 3;

                                return (
                                  <div
                                    key={ci}
                                    draggable
                                    onDragStart={(e) => {
                                      dragRef.current = { vi, ci };
                                      e.dataTransfer.effectAllowed = 'move';
                                      setTimeout(() => {
                                        e.target.style.opacity = '0.35';
                                        e.target.style.borderStyle = 'dashed';
                                      }, 0);
                                    }}
                                    onDragEnd={(e) => {
                                      e.target.style.opacity = '';
                                      e.target.style.borderStyle = '';
                                      dragRef.current = null;
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.style.background =
                                        '#e8f5ef';
                                      e.currentTarget.style.outline = `1.5px solid ${GREEN}`;
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.style.background = '';
                                      e.currentTarget.style.outline = '';
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.style.background = '';
                                      e.currentTarget.style.outline = '';
                                      if (!dragRef.current) return;
                                      const { vi: fromVi, ci: fromCi } =
                                        dragRef.current;
                                      if (fromVi !== vi) return;
                                      reorderCode(vi, fromCi, ci);
                                    }}
                                    style={{
                                      background: '#fff',
                                      borderBottom: '0.5px solid #f0f0ea',
                                      transition: 'background 0.1s',
                                    }}
                                  >
                                    {/* Code header row */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '11px 14px 8px 12px',
                                      }}
                                    >
                                      {/* Drag handle */}
                                      <div
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: '1fr 1fr',
                                          gap: 3,
                                          padding: '4px 5px',
                                          borderRadius: 4,
                                          cursor: 'grab',
                                          color: '#ccc',
                                          flexShrink: 0,
                                        }}
                                        title="Drag to reorder"
                                      >
                                        {[0, 1, 2, 3, 4, 5].map((d) => (
                                          <span
                                            key={d}
                                            style={{
                                              width: 3,
                                              height: 3,
                                              borderRadius: '50%',
                                              background: 'currentColor',
                                              display: 'block',
                                            }}
                                          />
                                        ))}
                                      </div>
                                      {/* Priority badge */}
                                      <div
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: '#fff',
                                          background: '#b4b2a9',
                                          borderRadius: '50%',
                                          width: 16,
                                          height: 16,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {ci + 1}
                                      </div>
                                      {editingCode?.vi === vi &&
                                      editingCode?.ci === ci ? (
                                        <>
                                          <input
                                            style={{
                                              ...inp,
                                              width: 70,
                                              fontFamily: 'monospace',
                                              fontSize: 12,
                                              padding: '3px 8px',
                                              flexShrink: 0,
                                            }}
                                            value={editCodeNum}
                                            autoFocus
                                            onChange={(e) =>
                                              setEditCodeNum(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter')
                                                saveEditCode();
                                              if (e.key === 'Escape')
                                                cancelEditCode();
                                            }}
                                          />
                                          <input
                                            style={{
                                              ...inp,
                                              flex: 1,
                                              minWidth: 0,
                                              fontSize: 13,
                                            }}
                                            value={editCodeDesc}
                                            onChange={(e) =>
                                              setEditCodeDesc(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter')
                                                saveEditCode();
                                              if (e.key === 'Escape')
                                                cancelEditCode();
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 500,
                                              color: GREEN,
                                              cursor: 'pointer',
                                              flexShrink: 0,
                                            }}
                                            onClick={saveEditCode}
                                          >
                                            Save
                                          </span>
                                          <span
                                            style={{
                                              fontSize: 12,
                                              color: '#999',
                                              cursor: 'pointer',
                                              flexShrink: 0,
                                            }}
                                            onClick={cancelEditCode}
                                          >
                                            Cancel
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span
                                            style={{
                                              background: '#f5f5f3',
                                              border: '0.5px solid #e0e0da',
                                              borderRadius: 6,
                                              padding: '3px 8px',
                                              fontSize: 12,
                                              color: '#555',
                                              fontFamily: 'monospace',
                                              flexShrink: 0,
                                            }}
                                          >
                                            {c.code}
                                          </span>
                                          <span
                                            style={{
                                              fontSize: 13,
                                              color: '#111',
                                              flex: 1,
                                              minWidth: 0,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {c.desc}
                                          </span>
                                          <span
                                            style={{
                                              color: '#ccc',
                                              cursor: 'pointer',
                                              fontSize: 13,
                                              flexShrink: 0,
                                            }}
                                            title="Edit code"
                                            onClick={() =>
                                              startEditCode(
                                                vi,
                                                ci,
                                                c.code,
                                                c.desc,
                                              )
                                            }
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.color =
                                                '#888')
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.color =
                                                '#ccc')
                                            }
                                          >
                                            ✎
                                          </span>
                                          <span
                                            style={{
                                              color: '#e0e0da',
                                              cursor: 'pointer',
                                              fontSize: 18,
                                              lineHeight: 1,
                                              flexShrink: 0,
                                            }}
                                            onClick={() => removeCode(vi, ci)}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.color =
                                                '#aaa')
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.color =
                                                '#e0e0da')
                                            }
                                          >
                                            ×
                                          </span>
                                        </>
                                      )}
                                    </div>

                                    {/* Rules list (optional conditions) */}
                                    <div
                                      style={{
                                        padding: '10px 14px 4px 38px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                      }}
                                    >
                                      {rules.map((rule, ri) => {
                                        const isEditingThis =
                                          editingRule?.vi === vi &&
                                          editingRule?.ci === ci &&
                                          editingRule?.ri === ri;
                                        const isDeletingThis =
                                          deletingRule?.vi === vi &&
                                          deletingRule?.ci === ci &&
                                          deletingRule?.ri === ri;

                                        if (isDeletingThis) {
                                          return (
                                            <div
                                              key={ri}
                                              style={{
                                                padding: '8px 10px',
                                                background: '#fff5f5',
                                                border: '0.5px solid #fca5a5',
                                                borderRadius: 7,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: 12,
                                                  color: '#888',
                                                  marginBottom: 8,
                                                }}
                                              >
                                                Delete this rule?
                                              </div>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  gap: 6,
                                                }}
                                              >
                                                <button
                                                  style={{
                                                    ...btnSecondary,
                                                    fontSize: 11,
                                                    padding: '3px 10px',
                                                  }}
                                                  onClick={() =>
                                                    setDeletingRule(null)
                                                  }
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  style={{
                                                    ...btnPrimary,
                                                    background: '#ef4444',
                                                    fontSize: 11,
                                                    padding: '3px 10px',
                                                  }}
                                                  onClick={confirmDeleteRule}
                                                >
                                                  Yes, delete
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        }

                                        if (isEditingThis) {
                                          return (
                                            <div
                                              key={ri}
                                              style={{
                                                background: '#fff',
                                                border: `0.5px solid ${GREEN}`,
                                                borderRadius: 7,
                                                padding: '8px 10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 6,
                                              }}
                                            >
                                              <textarea
                                                style={{
                                                  ...inp,
                                                  width: '100%',
                                                  boxSizing: 'border-box',
                                                  resize: 'none',
                                                  fontSize: 12,
                                                  lineHeight: 1.5,
                                                  rows: 2,
                                                }}
                                                value={editRuleText}
                                                maxLength={200}
                                                rows={2}
                                                onChange={(e) =>
                                                  setEditRuleText(
                                                    e.target.value,
                                                  )
                                                }
                                                autoFocus
                                              />
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent: 'flex-end',
                                                  gap: 6,
                                                  alignItems: 'center',
                                                }}
                                              >
                                                {editRuleText.length > 160 && (
                                                  <span
                                                    style={{
                                                      fontSize: 11,
                                                      color:
                                                        editRuleText.length >
                                                        190
                                                          ? '#ef4444'
                                                          : '#aaa',
                                                    }}
                                                  >
                                                    {200 - editRuleText.length}{' '}
                                                    left
                                                  </span>
                                                )}
                                                <button
                                                  style={{
                                                    ...btnSecondary,
                                                    fontSize: 11,
                                                    padding: '3px 10px',
                                                  }}
                                                  onClick={() =>
                                                    setEditingRule(null)
                                                  }
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  style={{
                                                    ...btnPrimary,
                                                    fontSize: 11,
                                                    padding: '3px 10px',
                                                  }}
                                                  onClick={saveEditRule}
                                                >
                                                  Save
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div
                                            key={ri}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'flex-start',
                                              gap: 6,
                                              padding: '8px 10px',
                                              background: '#f8f8f6',
                                              border: '0.5px solid #e8e8e3',
                                              borderRadius: 7,
                                              cursor: 'text',
                                            }}
                                            onClick={() =>
                                              startEditRule(vi, ci, ri, rule)
                                            }
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.background =
                                                '#f3f3f0')
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.background =
                                                '#f8f8f6')
                                            }
                                          >
                                            <span
                                              style={{
                                                fontSize: 13,
                                                color: '#111',
                                                flex: 1,
                                                lineHeight: 1.5,
                                              }}
                                            >
                                              {rule}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 16,
                                                lineHeight: 1,
                                                color: '#ccc',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                paddingLeft: 4,
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                startDeleteRule(vi, ci, ri);
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.currentTarget.style.color =
                                                  '#ef4444')
                                              }
                                              onMouseLeave={(e) =>
                                                (e.currentTarget.style.color =
                                                  '#ccc')
                                              }
                                            >
                                              ×
                                            </span>
                                          </div>
                                        );
                                      })}

                                      {/* Add rule / limit / inline add form */}
                                      {addingRule?.vi === vi &&
                                      addingRule?.ci === ci ? (
                                        <div
                                          style={{
                                            background: '#fff',
                                            border: `0.5px solid ${GREEN}`,
                                            borderRadius: 7,
                                            padding: '8px 10px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                          }}
                                        >
                                          <textarea
                                            style={{
                                              ...inp,
                                              width: '100%',
                                              boxSizing: 'border-box',
                                              resize: 'none',
                                              fontSize: 12,
                                              lineHeight: 1.5,
                                            }}
                                            placeholder="e.g. Only bill if 15+ min documented"
                                            value={newRuleText}
                                            maxLength={200}
                                            rows={2}
                                            onChange={(e) =>
                                              setNewRuleText(e.target.value)
                                            }
                                            autoFocus
                                          />
                                          <div
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'flex-end',
                                              gap: 6,
                                              alignItems: 'center',
                                            }}
                                          >
                                            {newRuleText.length > 160 && (
                                              <span
                                                style={{
                                                  fontSize: 11,
                                                  color:
                                                    newRuleText.length > 190
                                                      ? '#ef4444'
                                                      : '#aaa',
                                                }}
                                              >
                                                {200 - newRuleText.length} left
                                              </span>
                                            )}
                                            <button
                                              style={{
                                                ...btnSecondary,
                                                fontSize: 11,
                                                padding: '3px 10px',
                                              }}
                                              onClick={() =>
                                                setAddingRule(null)
                                              }
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              style={{
                                                ...btnPrimary,
                                                fontSize: 11,
                                                padding: '3px 10px',
                                              }}
                                              onClick={confirmAddRule}
                                            >
                                              Save
                                            </button>
                                          </div>
                                        </div>
                                      ) : atRuleLimit ? (
                                        <div
                                          style={{
                                            fontSize: 11,
                                            color: '#ccc',
                                            padding: '5px 8px',
                                            textAlign: 'center',
                                            border: '0.5px dashed #e8e8e3',
                                            borderRadius: 7,
                                          }}
                                        >
                                          Maximum 3 rules per code
                                        </div>
                                      ) : (
                                        <button
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '6px 8px',
                                            fontSize: 12,
                                            color: '#999',
                                            cursor: 'pointer',
                                            border: '0.5px dashed #d0d0ca',
                                            borderRadius: 7,
                                            background: 'none',
                                            width: '100%',
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.color =
                                              '#555')
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.color =
                                              '#999')
                                          }
                                          onClick={() => startAddRule(vi, ci)}
                                        >
                                          <div
                                            style={{
                                              width: 14,
                                              height: 14,
                                              borderRadius: '50%',
                                              background: '#e8f0fb',
                                              color: '#3b5bdb',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: 11,
                                              flexShrink: 0,
                                            }}
                                          >
                                            +
                                          </div>
                                          Add rule
                                        </button>
                                      )}
                                    </div>

                                    {/* Bottom padding */}
                                    <div style={{ height: 10 }} />
                                  </div>
                                );
                              })}

                              {/* Add CPT code button */}
                              <div
                                onClick={() => setShowCodeModal({ vi })}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '10px 14px 10px 14px',
                                  cursor: 'pointer',
                                  color: '#bbb',
                                  fontSize: 13,
                                  borderTop: '0.5px dashed #e8e8e3',
                                  background: '#fafaf8',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = '#555')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = '#bbb')
                                }
                              >
                                <div
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: '#e8f0fb',
                                    border: '0.5px solid #c5d5f7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 13,
                                    color: '#3b5bdb',
                                    flexShrink: 0,
                                  }}
                                >
                                  +
                                </div>
                                Add CPT code
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
